import React from 'react';
import { styled } from 'styled-components';
import { z } from 'zod';
import type { BlockDefinition } from '../../types';
import {
  IaGlobalStyle, IA_THEME_BOOTSTRAP, Wrap, Section, Eyebrow, Lede, Btn, Card,
  Icon, IaLogo, Grid, SectionHead,
} from './shared';

/**
 * IntelliAuth marketing blocks — the IntelliAuth tenant's own components.
 *
 * One block per homepage section (nav · hero · features · social-proof · steps ·
 * pricing · final-CTA · footer). They are referenced ONLY by the IntelliAuth
 * site's page content, so Cymmetri and Bastion never render them. Look & feel is
 * ported from intelliauth.io; dark/light/system is handled by the `.ia-dark`
 * class (see shared.tsx + apps/web entry-client `setupIaTheme`).
 */

const linkSchema = z.object({
  label: z.string(),
  href: z.string().default('#'),
  newTab: z.boolean().default(false),
});
// Footer items were originally stored as bare strings (no URL). Accept those and
// coerce to a link so already-published content still validates & renders (the
// registry drops any block whose props fail safeParse), while new/edited content
// carries label + href + newTab.
const footerLink = z.preprocess(
  (v) => (typeof v === 'string' ? { label: v, href: '#', newTab: false } : v),
  linkSchema,
);
const nl = (label: string, href = '#') => ({ label, href, newTab: false });
// Anchor attributes for "open in a new tab".
const ext = (n?: boolean) => (n ? { target: '_blank' as const, rel: 'noopener noreferrer' } : {});

// ── 1. Nav ─────────────────────────────────────────────────────────────────
export const iaNavSchema = z.object({
  links: z.array(linkSchema).default([
    nl('Features', '#features'),
    nl('Pricing', '#pricing'),
    nl('Docs'),
    nl('Blog'),
  ]),
  loginLabel: z.string().default('Log in'),
  loginHref: z.string().default('#'),
  loginNewTab: z.boolean().default(false),
  ctaLabel: z.string().default('Get Started'),
  ctaHref: z.string().default('#'),
  ctaNewTab: z.boolean().default(false),
  ctaCalendly: z.boolean().default(false),
});

const Nav = styled.nav`
  position: sticky; top: 0; z-index: 50;
  border-bottom: 1px solid var(--ia-border);
  background: color-mix(in srgb, var(--ia-bg) 80%, transparent);
  backdrop-filter: blur(20px);
`;
const NavRow = styled.div`
  height: 64px; display: flex; align-items: center; justify-content: space-between;
`;
const NavLinks = styled.div`
  display: none; align-items: center; gap: 32px;
  @media (min-width: 768px) { display: flex; }
  a { font-size: 13px; font-weight: 500; color: var(--ia-text2); transition: color .15s ease; }
  a:hover { color: var(--ia-text); }
`;
const NavRight = styled.div`display: flex; align-items: center; gap: 12px;`;
const NavLogin = styled.a`
  font-size: 13px; font-weight: 500; color: var(--ia-text2);
  &:hover { color: var(--ia-text); }
  @media (max-width: 480px) { display: none; }
`;
const ThemeSwitch = styled.div`
  display: inline-flex; align-items: center; gap: 2px; padding: 2px;
  border: 1px solid var(--ia-border); border-radius: 6px; background: var(--ia-card);
  button {
    display: inline-flex; align-items: center; justify-content: center;
    height: 28px; width: 28px; border: 0; border-radius: 4px; background: transparent;
    color: var(--ia-text3); cursor: pointer; transition: color .15s ease, background .15s ease;
  }
  button:hover { color: var(--ia-text); }
  button[aria-checked='true'] { background: color-mix(in srgb, var(--ia-text) 8%, transparent); color: var(--ia-text); }
`;

function IaNav(p: z.infer<typeof iaNavSchema>) {
  return (
    <Nav>
      {/* Emitted once with the first block: theme tokens + anti-FOUC bootstrap. */}
      <IaGlobalStyle />
      <script dangerouslySetInnerHTML={{ __html: IA_THEME_BOOTSTRAP }} suppressHydrationWarning />
      <Wrap>
        <NavRow>
          <IaLogo size={32} />
          <NavLinks>
            {p.links.map((l, i) => (
              <a key={i} href={l.href} {...ext(l.newTab)}>{l.label}</a>
            ))}
          </NavLinks>
          <NavRight>
            <ThemeSwitch role="radiogroup" aria-label="Theme">
              <button type="button" role="radio" aria-checked="false" aria-label="Light" data-ia-theme-set="light"><Icon name="sun" size={16} /></button>
              <button type="button" role="radio" aria-checked="false" aria-label="Dark" data-ia-theme-set="dark"><Icon name="moon" size={16} /></button>
              <button type="button" role="radio" aria-checked="true" aria-label="System" data-ia-theme-set="system"><Icon name="monitor" size={16} /></button>
            </ThemeSwitch>
            <NavLogin href={p.loginHref} {...ext(p.loginNewTab)}>{p.loginLabel}</NavLogin>
            <Btn href={p.ctaCalendly ? '#calendly' : p.ctaHref} {...(p.ctaCalendly ? {} : ext(p.ctaNewTab))} $variant="primary" $size="md">{p.ctaLabel}</Btn>
          </NavRight>
        </NavRow>
      </Wrap>
    </Nav>
  );
}
export const iaNavBlock: BlockDefinition = { type: 'iaNav', schema: iaNavSchema, Renderer: IaNav };

// ── 2. Hero ─────────────────────────────────────────────────────────────────
export const iaHeroSchema = z.object({
  badge: z.string().default('Now with Visual Flow Builder'),
  titleTop: z.string().default('Authentication that'),
  titleGradient: z.string().default('scales with you'),
  description: z.string().default(
    'Build secure, customizable login flows in minutes. Enterprise-grade CIAM with full data residency compliance and multi-tenant isolation.',
  ),
  primaryLabel: z.string().default('Start Free Trial'),
  primaryHref: z.string().default('#'),
  primaryNewTab: z.boolean().default(false),
  primaryCalendly: z.boolean().default(false),
  secondaryLabel: z.string().default('View Demo'),
  secondaryHref: z.string().default('#'),
  secondaryNewTab: z.boolean().default(false),
  secondaryCalendly: z.boolean().default(false),
  bullets: z.array(z.string()).default(['No credit card required', '30-day free trial', 'SOC 2 Compliant']),
});

const HeroBg = styled.div`
  pointer-events: none; position: absolute; inset: 0; overflow: hidden; z-index: 0;
  span { position: absolute; border-radius: 9999px; filter: blur(120px); }
  span.a { top: -40%; left: -20%; height: 80%; width: 80%; background: var(--ia-glow-a); }
  span.b { right: -10%; bottom: -30%; height: 60%; width: 60%; background: var(--ia-glow-b); }
  span.c { top: 20%; right: 10%; height: 40%; width: 40%; background: var(--ia-glow-c); }
`;
const HeroSec = styled(Section)`position: relative; text-align: center; overflow: hidden;`;
const Badge = styled.div`
  display: inline-flex; align-items: center; gap: 8px; margin-bottom: 32px;
  border: 1px solid var(--ia-border); border-radius: 9999px;
  background: color-mix(in srgb, var(--ia-card) 80%, transparent);
  padding: 6px 16px; font-size: 13px; color: var(--ia-text); box-shadow: var(--ia-shadow-sm);
  svg { color: var(--ia-success); width: 16px; height: 16px; }
`;
const HeroTitle = styled.h1`
  font-size: 44px; line-height: 1.1; font-weight: 600; letter-spacing: -1px; margin: 0; color: var(--ia-text);
  @media (min-width: 640px) { font-size: 60px; }
  @media (min-width: 1024px) { font-size: 72px; }
  span { background: linear-gradient(90deg, var(--ia-primary), var(--ia-success)); -webkit-background-clip: text; background-clip: text; color: transparent; }
`;
const HeroDesc = styled.p`
  margin: 24px auto 0; max-width: 720px; font-size: 20px; line-height: 1.6; color: var(--ia-text2);
`;
const CtaRow = styled.div`
  margin-top: 40px; display: flex; flex-direction: column; gap: 16px; justify-content: center;
  @media (min-width: 640px) { flex-direction: row; }
`;
const Bullets = styled.div`
  margin-top: 48px; display: flex; flex-wrap: wrap; gap: 32px; justify-content: center;
  font-size: 13px; color: var(--ia-text2);
  div { display: inline-flex; align-items: center; gap: 8px; }
  svg { color: var(--ia-success); width: 16px; height: 16px; }
`;

function IaHero(p: z.infer<typeof iaHeroSchema>) {
  return (
    <HeroSec $pt={96} $pb={128}>
      <HeroBg><span className="a" /><span className="b" /><span className="c" /></HeroBg>
      <Wrap $max={1024} style={{ position: 'relative', zIndex: 1 }}>
        <Badge><Icon name="sparkles" size={16} />{p.badge}</Badge>
        <HeroTitle>{p.titleTop}<br /><span>{p.titleGradient}</span></HeroTitle>
        <HeroDesc>{p.description}</HeroDesc>
        <CtaRow>
          <Btn href={p.primaryCalendly ? '#calendly' : p.primaryHref} {...(p.primaryCalendly ? {} : ext(p.primaryNewTab))} $variant="primary" $size="lg">{p.primaryLabel}<Icon name="arrowRight" size={18} /></Btn>
          <Btn href={p.secondaryCalendly ? '#calendly' : p.secondaryHref} {...(p.secondaryCalendly ? {} : ext(p.secondaryNewTab))} $variant="outline" $size="lg">{p.secondaryLabel}</Btn>
        </CtaRow>
        <Bullets>
          {p.bullets.map((b, i) => (<div key={i}><Icon name="check" size={16} />{b}</div>))}
        </Bullets>
      </Wrap>
    </HeroSec>
  );
}
export const iaHeroBlock: BlockDefinition = { type: 'iaHero', schema: iaHeroSchema, Renderer: IaHero };

// ── 3. Features ──────────────────────────────────────────────────────────────
export const iaFeaturesSchema = z.object({
  heading: z.string().default('Everything you need for modern authentication'),
  subheading: z.string().default('Built on proven open-source stack with enterprise features on top'),
  items: z.array(z.object({ icon: z.string().default('shield'), title: z.string(), description: z.string() })).default([
    { icon: 'workflow', title: 'Visual Flow Builder', description: 'Design authentication flows with our drag-and-drop canvas. No coding required.' },
    { icon: 'building', title: 'Multi-Tenant Management', description: 'Built for SaaS. Create isolated environments with separate branding and configurations.' },
    { icon: 'shield', title: 'Enterprise SSO', description: 'SAML, OIDC, and social logins. Connect to any identity provider.' },
    { icon: 'code', title: 'Custom Actions', description: 'Extend flows with JavaScript. Enrich profiles, call APIs, add custom logic.' },
    { icon: 'lock', title: 'Advanced Security', description: 'MFA, brute force protection, anomaly detection. Security built-in.' },
    { icon: 'terminal', title: 'Developer First', description: 'SDKs, APIs, webhooks, and comprehensive documentation.' },
  ]),
});

const FeatureCard = styled(Card)`
  padding: 24px; transition: background .15s ease, border-color .15s ease;
  &:hover { border-color: color-mix(in srgb, var(--ia-success) 40%, transparent); }
  .ic { display: inline-flex; align-items: center; justify-content: center; height: 48px; width: 48px; border-radius: 8px;
    background: color-mix(in srgb, var(--ia-success) 10%, transparent); color: var(--ia-success); margin-bottom: 16px; }
  h3 { font-size: 18px; font-weight: 600; color: var(--ia-text); margin: 0; }
  p { margin: 8px 0 0; font-size: 13px; line-height: 1.6; color: var(--ia-text2); }
`;

function IaFeatures(p: z.infer<typeof iaFeaturesSchema>) {
  return (
    <Section id="features">
      <Wrap>
        <SectionHead><Eyebrow>{p.heading}</Eyebrow><Lede>{p.subheading}</Lede></SectionHead>
        <Grid $cols={3}>
          {p.items.map((it, i) => (
            <FeatureCard key={i}>
              <span className="ic"><Icon name={it.icon} size={24} /></span>
              <h3>{it.title}</h3>
              <p>{it.description}</p>
            </FeatureCard>
          ))}
        </Grid>
      </Wrap>
    </Section>
  );
}
export const iaFeaturesBlock: BlockDefinition = { type: 'iaFeatures', schema: iaFeaturesSchema, Renderer: IaFeatures };

// ── 4. Social proof (stats + testimonials) ───────────────────────────────────
export const iaProofSchema = z.object({
  heading: z.string().default('Trusted by developers worldwide'),
  stats: z.array(z.object({ value: z.string(), label: z.string() })).default([
    { value: '10,000+', label: 'Organizations' },
    { value: '50M+', label: 'Monthly Authentications' },
    { value: '99.9%', label: 'Uptime SLA' },
  ]),
  testimonials: z.array(z.object({ quote: z.string(), name: z.string(), title: z.string() })).default([
    { quote: 'IntelliAuth made it incredibly easy to add multi-tenant auth to our SaaS. We went from zero to production in a week.', name: 'Sarah Chen', title: 'CTO, TechStart' },
    { quote: 'The flow builder is a game changer. Our non-technical team can now create and modify auth flows without developer help.', name: 'Marcus Johnson', title: 'Product Lead, CloudBase' },
    { quote: 'Data residency compliance was a hard requirement for us. IntelliAuth handled it out of the box with regional deployments.', name: 'Priya Sharma', title: 'Security Lead, DataFlow' },
  ]),
});

const StatTile = styled(Card)`
  padding: 32px 0; text-align: center;
  .v { font-size: 36px; font-weight: 700; background: linear-gradient(90deg, var(--ia-primary), var(--ia-success)); -webkit-background-clip: text; background-clip: text; color: transparent; }
  .l { margin-top: 8px; color: var(--ia-text2); }
`;
const Quote = styled(Card)`
  padding: 24px;
  p { font-size: 13px; font-style: italic; line-height: 1.6; color: var(--ia-text2); margin: 0; }
  .who { margin-top: 16px; display: flex; align-items: center; gap: 12px; }
  .av { height: 40px; width: 40px; border-radius: 9999px; display: flex; align-items: center; justify-content: center;
    background: var(--ia-primary); color: var(--ia-primary-fg); font-size: 13px; font-weight: 600; }
  .nm { font-size: 13px; font-weight: 500; color: var(--ia-text); margin: 0; }
  .tt { font-size: 12px; color: var(--ia-text3); margin: 0; }
`;
const initials = (n: string) => n.split(' ').map((s) => s[0]).join('');

function IaProof(p: z.infer<typeof iaProofSchema>) {
  return (
    <Section>
      <Wrap>
        <SectionHead><Eyebrow>{p.heading}</Eyebrow></SectionHead>
        <Grid $cols={3} style={{ marginBottom: 64 }}>
          {p.stats.map((s, i) => (<StatTile key={i}><div className="v">{s.value}</div><div className="l">{s.label}</div></StatTile>))}
        </Grid>
        <Grid $cols={3}>
          {p.testimonials.map((t, i) => (
            <Quote key={i}>
              <p>&ldquo;{t.quote}&rdquo;</p>
              <div className="who"><span className="av">{initials(t.name)}</span><div><p className="nm">{t.name}</p><p className="tt">{t.title}</p></div></div>
            </Quote>
          ))}
        </Grid>
      </Wrap>
    </Section>
  );
}
export const iaProofBlock: BlockDefinition = { type: 'iaProof', schema: iaProofSchema, Renderer: IaProof };

// ── 5. How it works (steps) ──────────────────────────────────────────────────
export const iaStepsSchema = z.object({
  heading: z.string().default('Get started in minutes'),
  steps: z.array(z.object({ icon: z.string().default('users'), title: z.string(), description: z.string() })).default([
    { icon: 'users', title: 'Create your organization', description: 'Sign up and set up your organization in under a minute. No credit card needed.' },
    { icon: 'workflow', title: 'Configure authentication', description: 'Use the visual flow builder to design your login, registration, and MFA flows.' },
    { icon: 'globe', title: 'Go live', description: 'Integrate with your app using our SDKs and deploy to any region worldwide.' },
  ]),
  ctaLabel: z.string().default('Start building for free'),
  ctaHref: z.string().default('#'),
  ctaNewTab: z.boolean().default(false),
  ctaCalendly: z.boolean().default(false),
});

const Step = styled.div`
  text-align: center;
  .ic { margin: 0 auto 24px; height: 64px; width: 64px; border-radius: 9999px; display: flex; align-items: center; justify-content: center;
    border: 1px solid var(--ia-border); background: var(--ia-card); color: var(--ia-primary); box-shadow: var(--ia-shadow-sm); }
  .n { margin: 0 auto 16px; height: 32px; width: 32px; border-radius: 9999px; display: inline-flex; align-items: center; justify-content: center;
    background: var(--ia-success); color: var(--ia-inv); font-size: 13px; font-weight: 600; }
  h3 { font-size: 18px; font-weight: 600; color: var(--ia-text); margin: 0; }
  p { margin: 8px 0 0; font-size: 13px; line-height: 1.6; color: var(--ia-text2); }
`;

function IaSteps(p: z.infer<typeof iaStepsSchema>) {
  return (
    <Section>
      <Wrap>
        <SectionHead><Eyebrow>{p.heading}</Eyebrow></SectionHead>
        <Grid $cols={3} $gap={48}>
          {p.steps.map((s, i) => (
            <Step key={i}>
              <div className="ic"><Icon name={s.icon} size={28} /></div>
              <div className="n">{i + 1}</div>
              <h3>{s.title}</h3>
              <p>{s.description}</p>
            </Step>
          ))}
        </Grid>
        <div style={{ marginTop: 48, textAlign: 'center' }}>
          <Btn href={p.ctaCalendly ? '#calendly' : p.ctaHref} {...(p.ctaCalendly ? {} : ext(p.ctaNewTab))} $variant="primary" $size="lg">{p.ctaLabel}<Icon name="arrowRight" size={18} /></Btn>
        </div>
      </Wrap>
    </Section>
  );
}
export const iaStepsBlock: BlockDefinition = { type: 'iaSteps', schema: iaStepsSchema, Renderer: IaSteps };

// ── 6. Pricing ────────────────────────────────────────────────────────────────
export const iaPricingSchema = z.object({
  heading: z.string().default('Simple, transparent pricing'),
  subheading: z.string().default("Start free. Upgrade when you're ready."),
  plans: z.array(z.object({
    name: z.string(),
    price: z.string(),
    period: z.string().default(''),
    description: z.string(),
    features: z.array(z.string()).default([]),
    highlighted: z.boolean().default(false),
    cta: z.string().default('Get Started'),
    ctaHref: z.string().default('#'),
    ctaNewTab: z.boolean().default(false),
  })).default([
    { name: 'Free', price: '$0', period: 'Free forever', description: 'For hobby projects and evaluation', features: ['1,000 MAU', '1 Tenant', '1 Social Connection', 'Basic auth', '7-day logs'], highlighted: false, cta: 'Get Started Free', ctaHref: '#', ctaNewTab: false },
    { name: 'Starter', price: '$49', period: '/month', description: 'For small teams and startups', features: ['10,000 MAU', '3 Tenants', 'Unlimited Social', 'Custom branding', '5 Actions', '30-day logs'], highlighted: false, cta: 'Start Free Trial', ctaHref: '#', ctaNewTab: false },
    { name: 'Pro', price: '$199', period: '/month', description: 'For growing businesses', features: ['50,000 MAU', '10 Tenants', 'Custom domains', 'Advanced MFA', 'Unlimited Actions', '90-day logs', 'Priority support'], highlighted: true, cta: 'Start Free Trial', ctaHref: '#', ctaNewTab: false },
    { name: 'Enterprise', price: 'Custom', period: '', description: 'For large organizations', features: ['Unlimited MAU', 'Unlimited Tenants', 'Enterprise SSO', 'Dedicated infra', 'Custom SLA', 'Compliance reports', 'Account manager'], highlighted: false, cta: 'Contact Sales', ctaHref: '#', ctaNewTab: false },
  ]),
});

const PriceGrid = styled.div`
  display: grid; gap: 24px; grid-template-columns: repeat(1, minmax(0, 1fr));
  @media (min-width: 768px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (min-width: 1024px) { grid-template-columns: repeat(4, minmax(0, 1fr)); }
`;
const Plan = styled.div<{ $hl?: boolean }>`
  position: relative; display: flex; flex-direction: column; border-radius: 8px; padding: 24px;
  backdrop-filter: blur(16px); box-shadow: var(--ia-shadow-md); background: var(--ia-card);
  border: ${(p) => (p.$hl ? '2px solid var(--ia-success)' : '1px solid var(--ia-border)')};
  ${(p) => (p.$hl ? 'box-shadow: var(--ia-shadow-lg), 0 0 0 1px color-mix(in srgb, var(--ia-success) 30%, transparent);' : '')}
  .pop { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); border-radius: 9999px;
    background: var(--ia-success); color: var(--ia-inv); padding: 4px 12px; font-size: 12px; font-weight: 500; white-space: nowrap; }
  h3 { font-size: 18px; font-weight: 600; color: var(--ia-text); margin: 0; }
  .price { margin-top: 12px; } .price b { font-size: 30px; font-weight: 700; color: var(--ia-text); }
  .price .per { margin-left: 4px; font-size: 13px; color: var(--ia-text2); }
  .desc { margin: 8px 0 0; font-size: 13px; color: var(--ia-text2); }
  ul { list-style: none; margin: 24px 0 0; padding: 0; flex: 1; display: flex; flex-direction: column; gap: 12px; }
  li { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: var(--ia-text2); }
  li svg { margin-top: 2px; flex-shrink: 0; color: var(--ia-success); width: 16px; height: 16px; }
`;

function IaPricing(p: z.infer<typeof iaPricingSchema>) {
  return (
    <Section id="pricing">
      <Wrap>
        <SectionHead><Eyebrow>{p.heading}</Eyebrow><Lede>{p.subheading}</Lede></SectionHead>
        <PriceGrid>
          {p.plans.map((pl, i) => (
            <Plan key={i} $hl={pl.highlighted}>
              {pl.highlighted && <span className="pop">Most Popular</span>}
              <h3>{pl.name}</h3>
              <div className="price"><b>{pl.price}</b>{pl.period && <span className="per">{pl.period}</span>}</div>
              <p className="desc">{pl.description}</p>
              <ul>{pl.features.map((f, j) => (<li key={j}><Icon name="check" size={16} />{f}</li>))}</ul>
              <Btn href={pl.ctaHref} {...ext(pl.ctaNewTab)} $variant={pl.highlighted ? 'primary' : 'outline'} $size="md" $block style={{ marginTop: 32 }}>{pl.cta}</Btn>
            </Plan>
          ))}
        </PriceGrid>
      </Wrap>
    </Section>
  );
}
export const iaPricingBlock: BlockDefinition = { type: 'iaPricing', schema: iaPricingSchema, Renderer: IaPricing };

// ── 7. Final CTA ──────────────────────────────────────────────────────────────
export const iaCtaSchema = z.object({
  heading: z.string().default('Ready to secure your application?'),
  description: z.string().default('Start your free trial today. No credit card required.'),
  primaryLabel: z.string().default('Get Started Free'),
  primaryHref: z.string().default('#'),
  primaryNewTab: z.boolean().default(false),
  primaryCalendly: z.boolean().default(false),
  secondaryLabel: z.string().default('Talk to sales'),
  secondaryHref: z.string().default('#'),
  secondaryNewTab: z.boolean().default(false),
  secondaryCalendly: z.boolean().default(false),
});

const CtaCard = styled(Card)`
  padding: 48px; text-align: center; box-shadow: var(--ia-shadow-lg);
  background: color-mix(in srgb, var(--ia-card) 90%, transparent); border-radius: 16px;
  h2 { font-size: 30px; font-weight: 600; letter-spacing: -0.4px; color: var(--ia-text); margin: 0; @media (min-width: 640px) { font-size: 36px; } }
  p { margin: 16px 0 0; font-size: 18px; color: var(--ia-text2); }
  .row { margin-top: 32px; display: flex; flex-direction: column; gap: 16px; justify-content: center; @media (min-width: 640px) { flex-direction: row; } }
`;

function IaCta(p: z.infer<typeof iaCtaSchema>) {
  return (
    <Section>
      <Wrap $max={768}>
        <CtaCard>
          <h2>{p.heading}</h2>
          <p>{p.description}</p>
          <div className="row">
            <Btn href={p.primaryCalendly ? '#calendly' : p.primaryHref} {...(p.primaryCalendly ? {} : ext(p.primaryNewTab))} $variant="primary" $size="lg">{p.primaryLabel}</Btn>
            <Btn href={p.secondaryCalendly ? '#calendly' : p.secondaryHref} {...(p.secondaryCalendly ? {} : ext(p.secondaryNewTab))} $variant="outline" $size="lg">{p.secondaryLabel}</Btn>
          </div>
        </CtaCard>
      </Wrap>
    </Section>
  );
}
export const iaCtaBlock: BlockDefinition = { type: 'iaCta', schema: iaCtaSchema, Renderer: IaCta };

// ── 8. Footer ─────────────────────────────────────────────────────────────────
export const iaFooterSchema = z.object({
  tagline: z.string().default('Enterprise-grade identity platform built for modern SaaS applications.'),
  columns: z.array(z.object({ title: z.string(), items: z.array(footerLink).default([]) })).default([
    { title: 'Product', items: ['Features', 'Pricing', 'Integrations', 'Changelog'].map((l) => nl(l)) },
    { title: 'Resources', items: ['Documentation', 'API Reference', 'Blog', 'Status'].map((l) => nl(l)) },
    { title: 'Company', items: ['About', 'Careers', 'Contact', 'Press'].map((l) => nl(l)) },
    { title: 'Legal', items: ['Privacy', 'Terms', 'Security', 'GDPR'].map((l) => nl(l)) },
  ]),
  socials: z.array(z.object({ icon: z.string(), href: z.string().default('#'), newTab: z.boolean().default(false) })).default([
    { icon: 'twitter', href: '#', newTab: false }, { icon: 'github', href: '#', newTab: false }, { icon: 'linkedin', href: '#', newTab: false },
  ]),
  copyright: z.string().default('IntelliAuth. All rights reserved.'),
});

const Foot = styled.footer`
  border-top: 1px solid var(--ia-border); padding: 64px 0;
  .cols { display: grid; gap: 48px; grid-template-columns: repeat(2, minmax(0, 1fr));
    @media (min-width: 768px) { grid-template-columns: repeat(5, minmax(0, 1fr)); } }
  .brand p { margin: 16px 0 0; font-size: 13px; line-height: 1.6; color: var(--ia-text2); }
  h4 { margin: 0 0 16px; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ia-text); }
  ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }
  a { font-size: 13px; color: var(--ia-text2); } a:hover { color: var(--ia-text); }
  .bar { margin-top: 48px; padding-top: 32px; border-top: 1px solid var(--ia-border);
    display: flex; flex-direction: column; align-items: center; gap: 16px; justify-content: space-between;
    @media (min-width: 640px) { flex-direction: row; } }
  .bar p { font-size: 13px; color: var(--ia-text2); margin: 0; }
  .soc { display: flex; align-items: center; gap: 24px; } .soc a { color: var(--ia-text3); } .soc a:hover { color: var(--ia-text); }
`;

function IaFooter(p: z.infer<typeof iaFooterSchema>) {
  const year = 2026;
  return (
    <Foot>
      <Wrap>
        <div className="cols">
          <div className="brand"><IaLogo size={32} /><p>{p.tagline}</p></div>
          {p.columns.map((c, i) => (
            <div key={i}>
              <h4>{c.title}</h4>
              <ul>{c.items.map((it, j) => (<li key={j}><a href={it.href || '#'} {...ext(it.newTab)}>{it.label}</a></li>))}</ul>
            </div>
          ))}
        </div>
        <div className="bar">
          <p>&copy; {year} {p.copyright}</p>
          <div className="soc">
            {p.socials.map((s, i) => (<a key={i} href={s.href} {...ext(s.newTab)} aria-label={s.icon}><Icon name={s.icon} size={20} /></a>))}
          </div>
        </div>
      </Wrap>
    </Foot>
  );
}
export const iaFooterBlock: BlockDefinition = { type: 'iaFooter', schema: iaFooterSchema, Renderer: IaFooter };

// Convenience list for registration.
export const iaBlocks: BlockDefinition[] = [
  iaNavBlock, iaHeroBlock, iaFeaturesBlock, iaProofBlock, iaStepsBlock, iaPricingBlock, iaCtaBlock, iaFooterBlock,
];
